import { updateItem, patch, removeItem } from '@ngxs/store/operators';
import { MembersState } from './members/members.state';
import { AccountActions, ActivityActions, DialogActions, MemberActions, ServerEventsActions, ToastActions } from '@app/_store';
import { OrganizationService } from '@app/_services';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  Organization,
  OrganizationListItem,
  OrganizationStateModel,
  PaymentSettings,
  SearchResultItem,
  SwishSettings,
} from './organization.models';
import { OrganizationActions } from './organization.actions';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, tap, switchMap } from 'rxjs/operators';
import { Navigate } from '@ngxs/router-plugin';
import { UiServices } from '@app/_helpers';

@State<OrganizationStateModel>({
  name: 'organization',
  defaults: {
    organizations: undefined,
    current: undefined,
    registrationError: false,
    searchResult: undefined,
  },
  children: [MembersState],
})
@Injectable()
export class OrganizationState {
  constructor(
    private organizationService: OrganizationService,
    private uiServices: UiServices
  ) {}

  @Selector()
  static organizations(state: OrganizationStateModel): OrganizationListItem[] | undefined {
    return state.organizations;
  }

  @Selector()
  static searchResults(state: OrganizationStateModel): SearchResultItem[] | undefined {
    return state.searchResult;
  }

  @Selector()
  static current(state: OrganizationStateModel): Organization {
    return state.current;
  }

  @Selector()
  static hasRegistrationError(state: OrganizationStateModel): boolean {
    return state.registrationError;
  }

  @Action(OrganizationActions.LoadAll)
  loadOrganizations(ctx: StateContext<OrganizationStateModel>): Observable<OrganizationListItem[]> {
    return this.organizationService.getAll().pipe(
      map(organizationList => {
        ctx.patchState({
          organizations: organizationList.organizations.map(organization => {
            return {
              ...organization,
              removeDate: organization.removeDate ? new Date(organization.removeDate) : undefined,
            };
          }),
        });
        return organizationList.organizations;
      })
    );
  }

  @Action(OrganizationActions.View)
  view(ctx: StateContext<OrganizationStateModel>, action: OrganizationActions.View): Observable<Organization> {
    ctx.patchState({
      current: null,
    });
    return ctx.dispatch(new AccountActions.Initialize(action.organizationId)).pipe(
      switchMap(() => {
        return this.organizationService.getOverview(action.organizationId).pipe(
          tap(overview => {
            overview.childOrganizations.map(child => {
              child.activities.map(activity => {
                activity.productSelectionDeadline = activity.productSelectionDeadline
                  ? new Date(activity.productSelectionDeadline)
                  : undefined;
                activity.paymentDeadline = activity.paymentDeadline ? new Date(activity.paymentDeadline) : undefined;

                return activity;
              });
              return child;
            });

            ctx.patchState({
              current: {
                ...overview,
                removeDate: overview.removeDate ? new Date(overview.removeDate) : undefined,
                lastMemberRegistryUpdate: overview.lastMemberRegistryUpdate
                  ? new Date(overview.lastMemberRegistryUpdate)
                  : undefined,
                suggestMemberRegistryImport: overview.daysSinceLastMemberRegistryUpdate
                  ? overview.daysSinceLastMemberRegistryUpdate > 90
                  : false,
              },
            });
          }),
          concatMap(organization => ctx.dispatch(new ServerEventsActions.Connect()).pipe(map(() => organization))),
          concatMap(organization =>
            ctx.dispatch(new ServerEventsActions.SubscribeToOrganizationEvents()).pipe(map(() => organization))
          ),
          concatMap(organization => ctx.dispatch(new MemberActions.LoadAll()).pipe(map(() => organization))),
          concatMap(organization => ctx.dispatch(new ActivityActions.Init()).pipe(map(() => organization))),
          tap(organization => {
            if (organization.parentName && organization.parentName.length > 0)
              ctx.dispatch(new ActivityActions.LoadTemplates()).pipe(map(() => organization));

            return organization;
          })
        );
      })
    );
  }

  @Action(OrganizationActions.ClearImportSuggestion)
  clearImportSuggestion(ctx: StateContext<OrganizationStateModel>, action: OrganizationActions.ClearImportSuggestion) {
    if (action.save) {
      return this.organizationService.postponeImportReminder().pipe(
        tap(res => {
          ctx.setState(
            patch<OrganizationStateModel>({
              current: patch<Organization>({
                daysSinceLastMemberRegistryUpdate: 0,
                lastMemberRegistryUpdate: new Date(res),
                suggestMemberRegistryImport: false,
              }),
            })
          );
        })
      );
    } else {
      ctx.setState(
        patch<OrganizationStateModel>({
          current: patch<Organization>({
            daysSinceLastMemberRegistryUpdate: 0,
            lastMemberRegistryUpdate: new Date(),
            suggestMemberRegistryImport: false,
          }),
        })
      );
      return of();
    }
  }

  @Action(OrganizationActions.ClearCurrent)
  clearCurrent(ctx: StateContext<OrganizationStateModel>): Observable<void> {
    const id = ctx.getState().current?.id;
    ctx.patchState({
      current: undefined,
    });

    return ctx.dispatch(new ActivityActions.Reset()).pipe(
      concatMap(() => ctx.dispatch(new ServerEventsActions.UnsubscribeFromOrganizationEvents(id))),
      concatMap(() => ctx.dispatch(new ServerEventsActions.Disconnect())),
      switchMap(() => ctx.dispatch(new MemberActions.Reset()))
    );
  }

  @Action(OrganizationActions.UploadLogo)
  uploadLogo(ctx: StateContext<OrganizationStateModel>, action: OrganizationActions.UploadLogo): Observable<void> {
    return this.organizationService.uploadLogo(action.file).pipe(
      map(response => {
        const state = ctx.getState();

        ctx.setState(
          patch<OrganizationStateModel>({
            current: patch<Organization>({
              logoUrl: response.url,
              logoSize: {
                width: response.width,
                height: response.height,
              },
            }),
            organizations: updateItem<OrganizationListItem>(
              i => i.id == state.current.id || i.parent == state.current.id,
              patch({
                logoUrl: response.url,
                childOrganizations: updateItem(
                  () => true,
                  patch({
                    logoUrl: response.url,
                  })
                ),
              })
            ),
          })
        );
      }),
      catchError(() => {
        ctx.dispatch(
          new ToastActions.ShowError(`Ett fel uppstod när bilden skulle sparas. Bilden måste vara minst 400 pixlar bred och hög`)
        );
        return of(null);
      })
    );
  }

  @Action(OrganizationActions.Register)
  register(
    { dispatch, patchState }: StateContext<OrganizationStateModel>,
    action: OrganizationActions.Register
  ): Observable<void> {
    return this.organizationService.registerOrganization(action.form).pipe(
      map(org => {
        patchState({
          registrationError: false,
        });
        return org;
      }),
      map(org => {
        dispatch(new AccountActions.Initialize());
        return org;
      }),
      concatMap(() => {
        return dispatch(new OrganizationActions.LoadAll());
      }),
      concatMap(() => {
        return dispatch(new Navigate(['/']));
      }),
      catchError(() => {
        patchState({
          registrationError: true,
        });
        return of(null);
      })
    );
  }

  @Action(OrganizationActions.HandleAdministratorRequest)
  handleAdministratorRequest(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.HandleAdministratorRequest
  ): Observable<void> {
    const call = action.approve
      ? this.organizationService.approveAdministratorRequest(action.user.id)
      : this.organizationService.denyAdministratorRequest(action.user.id);

    return call.pipe(
      tap(() => {
        ctx.setState(
          patch<OrganizationStateModel>({
            current: patch<Organization>({
              administratorRequests: removeItem(x => x.id == action.user.id),
            }),
          })
        );
      }),
      tap(() => {
        ctx.dispatch(
          new ToastActions.ShowSuccess(
            action.approve
              ? `${action.user.name} har lagts till som administratör`
              : `Begäran från ${action.user.name} har tagits bort`
          )
        );
      })
    );
  }

  @Action(OrganizationActions.DeleteAdministrator)
  deleteAdministrator(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.DeleteAdministrator
  ): Observable<void> {
    return this.organizationService.deleteAdministrator(action.administrator.id).pipe(
      tap(() => {
        ctx.setState(
          patch<OrganizationStateModel>({
            current: patch<Organization>({
              administrators: removeItem(x => x.id == action.administrator.id),
            }),
          })
        );
      }),
      tap(() => {
        ctx.dispatch(new ToastActions.ShowSuccess(`${action.administrator.name} har tagits bort som administratör`));
      })
    );
  }

  @Action(OrganizationActions.UpdateOrganizationSettings)
  updateOrganizationSettings(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.UpdateOrganizationSettings
  ): Observable<void> {
    // The SMS reminder default only means something when SMS is enabled; otherwise leave the stored value alone
    const smsEnabled = ctx.getState().current?.smsEnabled === true;

    return this.organizationService
      .patchOrganization([
        {
          op: 'replace',
          path: 'organizationNumber',
          value: action.organizationNumber,
        },
        {
          op: 'replace',
          path: 'coAddress',
          value: action.coAddress,
        },
        {
          op: 'replace',
          path: 'streetAddress',
          value: action.streetAddress,
        },
        {
          op: 'replace',
          path: 'zipCode',
          value: action.zipCode,
        },
        {
          op: 'replace',
          path: 'city',
          value: action.city,
        },
        {
          op: 'replace',
          path: 'paymentOptions/swish/recipients',
          value: action.swishRecipients,
        },
        {
          op: 'remove',
          path: 'paymentOptions/swish/number',
          value: undefined,
        },
        {
          op: 'replace',
          path: 'paymentOptions/swish/prefix',
          value: action.swishPrefix,
        },
        {
          op: 'replace',
          path: 'paymentOptions/defaultPartialPaymentAllowed',
          value: action.defaultPartialPaymentAllowed,
        },
        ...(smsEnabled
          ? [
              {
                op: 'replace',
                path: 'paymentOptions/defaultSmsPaymentReminderEnabled',
                value: action.defaultSmsPaymentReminderEnabled,
              },
            ]
          : []),
      ])
      .pipe(
        map(() => {
          ctx.setState(
            patch<OrganizationStateModel>({
              current: patch<Organization>({
                organizationNumber: action.organizationNumber,
                coAddress: action.coAddress,
                streetAddress: action.streetAddress,
                zipCode: action.zipCode,
                city: action.city,
                paymentSettings: patch<PaymentSettings>({
                  swish: patch<SwishSettings>({
                    number: null,
                    recipients: action.swishRecipients,
                    prefix: action.swishPrefix,
                  }),
                  defaultPartialPaymentAllowed: action.defaultPartialPaymentAllowed,
                  ...(smsEnabled ? { defaultSmsPaymentReminderEnabled: action.defaultSmsPaymentReminderEnabled } : {}),
                }),
              }),
            })
          );
        }),
        tap(() => ctx.dispatch(new ToastActions.ShowSuccess('Inställningarna har uppdaterats')))
      );
  }

  @Action(OrganizationActions.UpdateDemoMode)
  updateDemoMode(ctx: StateContext<OrganizationStateModel>, action: OrganizationActions.UpdateDemoMode): Observable<void> {
    return this.organizationService
      .patchOrganization([
        {
          op: 'replace',
          path: 'demoMode',
          value: action.demoMode,
        },
      ])
      .pipe(
        map(() => {
          ctx.setState(
            patch<OrganizationStateModel>({
              current: patch<Organization>({
                demoMode: action.demoMode,
              }),
            })
          );
        }),
        tap(() =>
          ctx.dispatch(new ToastActions.ShowSuccess(action.demoMode ? 'Demoläget har slagits på' : 'Demoläget har stängts av'))
        )
      );
  }

  @Action(OrganizationActions.SearchOrganization)
  search(ctx: StateContext<OrganizationStateModel>, action: OrganizationActions.SearchOrganization): Observable<void> {
    return this.organizationService.search(action.term).pipe(
      map(searchResponse => {
        ctx.setState(
          patch<OrganizationStateModel>({
            searchResult: searchResponse.searchResult,
          })
        );
      })
    );
  }

  @Action(OrganizationActions.ClearSearchResults)
  clearSearchResults(ctx: StateContext<OrganizationStateModel>): Observable<void> {
    ctx.setState(
      patch<OrganizationStateModel>({
        searchResult: undefined,
      })
    );
    return of();
  }

  @Action(OrganizationActions.RequestAdministratorPrivileges)
  requestAdministratorPrivileges(
    ctx: StateContext<OrganizationStateModel>,
    action: OrganizationActions.RequestAdministratorPrivileges
  ) {
    return this.organizationService.requestAdministratorPrivileges(action.organizationId).pipe(
      tap(() => {
        ctx.setState(
          patch<OrganizationStateModel>({
            searchResult: updateItem(
              i => i.id == action.organizationId,
              patch({
                requestAlreadySent: true,
              })
            ),
          })
        );
      }),
      tap(() => {
        ctx.dispatch(new ToastActions.ShowSuccess(`Förfrågan har skickats till ${action.organizationName}`));
      }),
      catchError(err => {
        if (err.status == 409) {
          ctx.dispatch(new ToastActions.ShowSuccess(`Du har redan en utestående förfrågan till ${action.organizationName}`));
          return of();
        } else {
          return throwError(() => err);
        }
      })
    );
  }

  @Action(OrganizationActions.RetireOrganization)
  retireOrganization(ctx: StateContext<OrganizationStateModel>) {
    const current = ctx.getState().current;

    setTimeout(() => {
      return ctx.dispatch(
        new DialogActions.Confirm(
          `Avsluta ${current.name}`,
          `Är du <strong>VERKLIGEN</strong> säker på att du vill ta bort <strong>${current.name}</strong>?<br/><br/>Det går inte att ångra efter detta steg!`,
          () => {
            return this.organizationService.retire().pipe(
              concatMap(response => {
                ctx.setState(
                  patch<OrganizationStateModel>({
                    current: patch<Organization>({
                      removeDate: new Date(response.removeDate),
                    }),
                    organizations: updateItem(
                      x => x.id == current.id,
                      patch({
                        removeDate: new Date(response.removeDate),
                      })
                    ),
                  })
                );

                // this needs to be fixed so that child org is marked as removed without requiring a page reload
                // ctx.setState(
                //   patch<OrganizationStateModel>({
                //     organizations: updateItem<OrganizationListItem>(
                //       org => org.childOrganizations?.some(y => y.id == current.id),
                //       patch({
                //         childOrganizations: updateItem<OrganizationListItem>(
                //           child => child.id === current.id,
                //           patch({
                //             removeDate: new Date(response.removeDate),
                //           })
                //         ),
                //       })
                //     ),
                //   })
                // );

                return ctx.dispatch(new Navigate(['organization', ctx.getState().current.id, 'to-be-removed']));
              })
            );
          },
          true,
          'hmm',
          true
        )
      );
    }, 500);
  }

  @Action(OrganizationActions.LoadRetiredFiles)
  loadRetiredFiles(ctx: StateContext<OrganizationStateModel>) {
    return this.organizationService.getRetiredDownloads().pipe(
      map(files => {
        ctx.setState(
          patch<OrganizationStateModel>({
            current: patch<Organization>({
              retiredFiles: files,
            }),
          })
        );
      })
    );
  }

  @Action(OrganizationActions.DownloadRetiredFiles)
  downloadRetiredFiles(ctx: StateContext<OrganizationStateModel>) {
    const current = ctx.getState().current;
    return this.organizationService
      .getRetiredDownloadsZip()
      .pipe(tap(archiveZip => this.uiServices.downloadFile(archiveZip, `${current.name}.zip`, 'application/octet-stream')));
  }
}
